import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ManagerPinModal from "@/components/ManagerPinModal";
import { Receipt, Plus, Trash2, TrendingDown, AlertTriangle, Wallet } from "lucide-react";

const blank = { date: new Date().toISOString().slice(0, 10), category: "General", vendor: "", amount: 0, payment_method: "Cash", note: "", paid_from_drawer: false };

export default function Expenses() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [shiftRequired, setShiftRequired] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const load = () => api.get("/expenses", { params: range }).then((r) => setList(r.data));
  useEffect(() => { api.get("/expenses/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { load(); }, [range]);

  const postExpense = async (pin) => {
    await api.post("/expenses", { ...form, amount: parseFloat(form.amount || 0), manager_pin: pin });
    toast.success("Expense recorded"); setPinOpen(false); setOpen(false); setForm(blank); load();
  };

  const save = async () => {
    if (!(parseFloat(form.amount) > 0)) { toast.error("Enter an amount greater than zero"); return; }
    try {
      await postExpense(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.shift_required) { setOpen(false); setShiftRequired(true); }
      else if (detail?.needs_approval) { setPinOpen(true); }
      else toast.error(apiError(detail));
    }
  };

  const submitPin = async (pin) => {
    try { await postExpense(pin); }
    catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.needs_approval) throw new Error(detail.message || "Invalid PIN");
      setPinOpen(false); toast.error(apiError(detail)); throw new Error("Failed");
    }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); load(); };
  const setMethod = (v) => setForm({ ...form, payment_method: v, paid_from_drawer: v === "Cash" ? form.paid_from_drawer : false });

  const total = list.reduce((s, e) => s + e.amount, 0);
  const byCat = {};
  list.forEach((e) => { byCat[e.category] = (byCat[e.category] || 0) + e.amount; });
  const topCats = Object.entries(byCat).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><Receipt className="w-7 h-7 text-rose-500" /> Expenses</h1>
          <p className="text-muted-foreground">Track what your business spends. Feeds directly into your profit reports.</p>
        </div>
        <Button onClick={() => { setForm(blank); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Expense</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-4 sm:col-span-1"><p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Total Spent</p><p className="mono text-2xl font-bold text-rose-500">{money(total)}</p><p className="text-xs text-muted-foreground mt-1">{list.length} entries</p></Card>
        {topCats.map(([c, v]) => (
          <Card key={c} className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">{c}</p><p className="mono text-xl font-bold">{money(v)}</p></Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">From</Label><DatePicker value={range.start} onChange={(v) => setRange({ ...range, start: v })} data-testid="exp-start" /></div>
        <div><Label className="text-xs">To</Label><DatePicker value={range.end} onChange={(v) => setRange({ ...range, end: v })} data-testid="exp-end" /></div>
        {(range.start || range.end) && <Button variant="ghost" onClick={() => setRange({ start: "", end: "" })}>Clear</Button>}
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Vendor</TableHead><TableHead>Method</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-xs">{fmtDate(e.date)}</TableCell>
                <TableCell><Badge variant="outline">{e.category}</Badge></TableCell>
                <TableCell>{e.vendor || "-"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.payment_method}{e.paid_from_drawer && <Badge className="ml-1.5 bg-amber-500/15 text-amber-600 border-amber-500/30 text-[10px]" data-testid={`drawer-badge-${e.id}`}>Drawer</Badge>}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{e.note}</TableCell>
                <TableCell className="text-right mono font-bold">{money(e.amount)}</TableCell>
                <TableCell><Button size="sm" variant="ghost" className="text-rose-500 h-7" onClick={() => del(e.id)} data-testid={`del-expense-${e.id}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No expenses in this range.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-form">
          <DialogHeader><DialogTitle className="font-head">Add Expense</DialogTitle>
            <DialogDescription>Record a business expense. Cash expenses can optionally be paid from the register drawer.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} data-testid="ef-date" /></div>
            <div><Label className="text-xs">Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="ef-category">
                {cats.map((c) => <option key={c}>{c}</option>)}
              </select></div>
            <div><Label className="text-xs">Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="ef-vendor" /></div>
            <div><Label className="text-xs">Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mono" data-testid="ef-amount" /></div>
            <div><Label className="text-xs">Payment Method</Label>
              <select value={form.payment_method} onChange={(e) => setMethod(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="ef-method">
                <option>Cash</option><option>Bank Transfer</option><option>WiPay/Card</option><option>Cheque</option>
              </select></div>
            <div><Label className="text-xs">Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="ef-note" /></div>
            {form.payment_method === "Cash" && (
              <div className="col-span-2 rounded-xl border border-border p-3" data-testid="ef-drawer-block">
                <Label className="text-xs flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Paid from Register Drawer?</Label>
                <div className="flex gap-2 mt-2">
                  <Button type="button" size="sm" variant={form.paid_from_drawer ? "default" : "outline"}
                    className={form.paid_from_drawer ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                    onClick={() => setForm({ ...form, paid_from_drawer: true })} data-testid="ef-drawer-yes">Yes</Button>
                  <Button type="button" size="sm" variant={!form.paid_from_drawer ? "default" : "outline"}
                    className={!form.paid_from_drawer ? "bg-slate-700 hover:bg-slate-800 text-white" : ""}
                    onClick={() => setForm({ ...form, paid_from_drawer: false })} data-testid="ef-drawer-no">No</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {form.paid_from_drawer
                    ? "Requires an open shift. Recorded as a Cash Removal that reduces Expected Cash in the drawer."
                    : "Cash paid outside the POS register. Does not affect the cashier drawer or Shift Report."}
                </p>
              </div>
            )}
          </div>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-expense">Save Expense</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={shiftRequired} onOpenChange={setShiftRequired}>
        <DialogContent className="max-w-sm" data-testid="expense-shift-required-modal">
          <DialogHeader className="sr-only"><DialogTitle>Shift Required</DialogTitle>
            <DialogDescription>A cashier shift must be open to pay a cash expense from the register drawer.</DialogDescription>
          </DialogHeader>
          <div className="text-center">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto"><AlertTriangle className="w-6 h-6" /></div>
            <p className="font-head font-extrabold text-xl mt-3">SHIFT REQUIRED</p>
            <p className="text-sm text-muted-foreground mt-1.5">You must open a cashier shift before paying a cash expense from the register drawer.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" onClick={() => setShiftRequired(false)} data-testid="expense-shift-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShiftRequired(false); nav("/shift"); }} data-testid="expense-shift-open">
              <Receipt className="w-4 h-4 mr-2" /> OPEN SHIFT
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)}
        title="Cash Removal Approval"
        reason="This cash-drawer expense exceeds the cashier limit. A Manager or Owner PIN is required."
        onSubmit={submitPin} />
    </div>
  );
}
